/* eslint-disable no-param-reassign */
import _ from 'underscore';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import createStore from 'vendor/cnpm/fluxible.v0-4/addons/createStore';

import getCurrentSocialProfile from 'pages/open-course/common/models/currentSocialProfile';

import * as discussionConstants from 'bundles/discussions/constants';
import { addAnswerFields, hydrateAnswers, hydrateQuestion } from 'bundles/discussions/utils/hydrateQuestionsAndAnswers';

const ANSWER_LIMIT = discussionConstants.answers.limitPerPage;
const COMMENT_LIMIT = discussionConstants.comments.limitPerPage;
const { loadingStates, answerSorts, defaults } = discussionConstants;

const getAnswerKey = function (page: $TSFixMe, sort: $TSFixMe) {
  return `${page}~${sort}`;
};

/**
 * Key for comments in the store. Currently just the page, but could later include sort.
 */
const getCommentKey = function (page: $TSFixMe) {
  return page;
};

const ThreadDetailsStore = createStore({
  storeName: 'ThreadDetailsStore',

  initialize() {
    this.data = {};
    this.comments = {};
    this.pageCount = {};
    this.commentPageCount = {};
    this.commentPageRange = {};
    this.savingStates = {};
    this.answerToInsert = null;
    this.questionLoadingState = {};
    this.answerLoadingState = {};
    this.commentLoadingState = {};
    this.forumType = undefined;
    this.highlightedPosts = {};
    this.retryCml = {};
  },

  getUserId() {
    return this.dispatcher.getStore('ApplicationStore').getUserData().id;
  },

  getQuestion(questionId: $TSFixMe) {
    return this.data[questionId] && this.data[questionId].thread;
  },

  getHighlightedPost(questionId: $TSFixMe) {
    return this.highlightedPosts[questionId];
  },

  getReplies(questionId: $TSFixMe, page: $TSFixMe, sort: $TSFixMe) {
    const key = getAnswerKey(page, sort);
    return this.data[questionId] && this.data[questionId][key];
  },

  getLoadedComments(answerId: $TSFixMe) {
    const comments = this.comments[answerId];

    if (comments) {
      return _(comments).chain().values().flatten().reverse().value();
    }

    return undefined;
  },

  getCommentPageRange(answerId: $TSFixMe) {
    return {
      totalPageCount: this.commentPageCount[answerId],
      startPage: this.commentPageRange[answerId] && this.commentPageRange[answerId].startPage,
      endPage: this.commentPageRange[answerId] && this.commentPageRange[answerId].endPage,
    };
  },

  getPageCount(questionId: $TSFixMe, page: $TSFixMe, sort: $TSFixMe) {
    const key = getAnswerKey(page, sort);
    return this.pageCount[questionId + key];
  },

  /**
    @param post a post Object (question, answer, or comment)
    @returns array of matching posts (duplicates come from our caching strategy)
   */
  findPosts(post: $TSFixMe, includeHighlighted: $TSFixMe) {
    let posts: $TSFixMe = [];

    if (post.type === 'question') {
      posts = [this.data[post.questionId].thread];
    } else if (post.type === 'answer') {
      const entries = this.data[post.questionId];

      posts = _(entries).reduce((accum, entry) => {
        const match = _(entry).findWhere({ id: post.id });
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
        if (match) accum.push(match);
        return accum;
      }, []);
    } else if (post.type === 'comment') {
      posts = [
        _(this.getLoadedComments(post.topLevelForumAnswerId)).findWhere({
          id: post.id,
        }),
      ];
    }

    if (includeHighlighted) {
      const highlightedPost = this.highlightedPosts[post.questionId];

      if (highlightedPost) {
        posts.push(highlightedPost);
      }
    }

    return posts;
  },

  isLoaded({ questionId, page, sort }: $TSFixMe) {
    const key = getAnswerKey(page, sort);
    const hasQuestion = !!this.data[questionId];

    if (hasQuestion) {
      if (!(key in this.data[questionId])) {
        return false;
      }
      const answers = this.data[questionId][key];
      const hasAnswer = !!answers;
      if (hasAnswer) {
        const commentsHaveLoaded = _(answers).every((ans) => {
          return ans.childAnswerCount > 0 ? this.comments[ans.topLevelForumAnswerId] : true;
        });
        return commentsHaveLoaded;
      } else {
        return true;
      }
    } else {
      return false;
    }
  },

  getAnswerLoadedState({ questionId, page, sort }: $TSFixMe) {
    return this.answerLoadingState[questionId + sort + page];
  },

  isHighlighted(post: $TSFixMe) {
    const highlightedPost = this.highlightedPosts[post.questionId];
    return highlightedPost && highlightedPost.id === post.id;
  },

  loadAnswersHelper(
    hydratedAnswers: $TSFixMe,
    topLevelAnswerCount: $TSFixMe,
    questionId: $TSFixMe,
    page: $TSFixMe,
    sort: $TSFixMe
  ) {
    if (!this.data[questionId]) {
      this.data[questionId] = {};
    }

    const key = getAnswerKey(page, sort);
    this.data[questionId][key] = hydratedAnswers;
    this.pageCount[questionId + key] = Math.ceil(topLevelAnswerCount / ANSWER_LIMIT);

    // Insert the user's new answer, if it's not in the page from the backend
    if (
      this.answerToInsert &&
      this.answerToInsert.questionId === questionId &&
      page === 1 &&
      sort === answerSorts.newestSort
    ) {
      const answerAlreadyPresent = !!_(this.data[questionId][key]).findWhere({
        id: this.answerToInsert.id,
      });
      if (!answerAlreadyPresent) {
        this.data[questionId][key].unshift(this.answerToInsert);
      }
      this.answerToInsert = null;
    }
  },

  loadCommentsHelper(hydratedComments: $TSFixMe, answerId: $TSFixMe, page: $TSFixMe) {
    if (!this.comments[answerId]) {
      this.comments[answerId] = {};
    }

    if (!this.commentPageRange[answerId]) {
      this.commentPageRange[answerId] = {
        startPage: page,
        endPage: page,
      };
    }

    const key = getCommentKey(page);
    this.comments[answerId][key] = hydratedComments;
  },

  loadHighlightedPostHelper(post: $TSFixMe, questionId: $TSFixMe) {
    post.highlighted = true;
    this.highlightedPosts[questionId] = post;
  },
});

ThreadDetailsStore.handlers = {
  LOAD_QUESTION({ response, questionId, forumType }: $TSFixMe) {
    if (!this.data[questionId]) {
      this.data[questionId] = {};
    }

    const { question, answers, highlightedPost } = hydrateQuestion(response, forumType);

    this.data[questionId].thread = question;
    this.forumType = forumType;

    if (highlightedPost) {
      this.loadHighlightedPostHelper(highlightedPost, questionId);
    }

    if (answers) {
      const topLevelAnswers = answers
        .filter((potentialAnswers: $TSFixMe) => potentialAnswers.type === 'answer')
        .sort((left: $TSFixMe, right: $TSFixMe) => left.order - right.order);
      this.loadAnswersHelper(topLevelAnswers, question.topLevelAnswerCount, questionId, 1, defaults.detailSort);

      answers.forEach((answer: $TSFixMe) => {
        const forumAnswerId = answer.topLevelForumAnswerId;

        if (answer.childAnswerCount > 0) {
          const comments = answers
            .filter(
              (potentialAnswers: $TSFixMe) =>
                potentialAnswers.topLevelForumAnswerId === forumAnswerId && potentialAnswers.type === 'comment'
            )
            .sort((left: $TSFixMe, right: $TSFixMe) => left.order - right.order);

          this.loadCommentsHelper(comments, forumAnswerId, 1);
          this.commentLoadingState[forumAnswerId] = loadingStates.DONE;
          this.commentPageCount[forumAnswerId] = Math.ceil(answer.childAnswerCount / COMMENT_LIMIT);
        }

        this.commentLoadingState[forumAnswerId] = loadingStates.DONE;
      });
    }

    this.answerLoadingState[questionId + defaults.detailSort + 1] = loadingStates.DONE;

    this.data[questionId].thread.questionId = questionId;
    this.data[questionId].thread.type = 'question';
    this.emitChange();
  },

  LOAD_ANSWERS_WITH_COMMENTS({ response, questionId, page, sort, forumType }: $TSFixMe) {
    if (!this.forumType) {
      this.forumType = forumType;
    }

    const answers = hydrateAnswers(response, this.forumType, questionId);
    const topLevelAnswers = answers
      .filter((potentialAnswers: $TSFixMe) => potentialAnswers.type === 'answer')
      .sort((left: $TSFixMe, right: $TSFixMe) => left.order - right.order);

    this.loadAnswersHelper(topLevelAnswers, response.paging.total, questionId, page, sort);

    answers.forEach((answer: $TSFixMe) => {
      const forumAnswerId = answer.topLevelForumAnswerId;
      if (answer.childAnswerCount > 0) {
        const comments = answers
          .filter(
            (potentialAnswers: $TSFixMe) =>
              potentialAnswers.topLevelForumAnswerId === forumAnswerId && potentialAnswers.type === 'comment'
          )
          .sort((left: $TSFixMe, right: $TSFixMe) => left.order - right.order);

        this.loadCommentsHelper(comments, forumAnswerId, 1);
        this.commentPageCount[forumAnswerId] = Math.ceil(answer.childAnswerCount / COMMENT_LIMIT);
      }

      this.commentLoadingState[forumAnswerId] = loadingStates.DONE;
    });

    this.answerLoadingState[questionId + sort + page] = loadingStates.DONE;

    this.emitChange();
  },

  LOAD_COMMENT_PAGES({ response, questionId, answerId, startPage }: $TSFixMe) {
    if (!this.comments[answerId]) {
      this.comments[answerId] = {};
    }

    const length = Math.ceil(response.elements.length / COMMENT_LIMIT);
    const endPage = startPage + (length - 1);

    this.commentPageCount[answerId] = Math.ceil(response.paging.total / COMMENT_LIMIT);

    if (!this.commentPageRange[answerId]) {
      this.commentPageRange[answerId] = {
        startPage: Infinity,
        endPage: 1,
      };
    }

    if (startPage < (this.commentPageRange[answerId].startPage || Infinity)) {
      this.commentPageRange[answerId].startPage = startPage;
    }

    if (endPage > this.commentPageRange[answerId].endPage) {
      this.commentPageRange[answerId].endPage = endPage;
    }

    const pagedResponses = _.range(0, length).map((pageNumber) => {
      const pageResponseElements = response.elements.slice(
        COMMENT_LIMIT * pageNumber,
        COMMENT_LIMIT * (pageNumber + 1)
      );

      return Object.assign({}, response, {
        elements: pageResponseElements,
      });
    });

    pagedResponses.forEach((pagedResponse, index) => {
      const page = index + startPage;

      this.loadCommentsHelper(hydrateAnswers(pagedResponse, this.forumType, questionId), answerId, page);
    });

    this.commentLoadingState[answerId] = loadingStates.DONE;

    this.emitChange();
  },

  RECEIVE_POST({ resp, data, parentPost, questionId, forumType }: $TSFixMe) {
    const newPostWithoutFields = Object.assign({}, resp.elements[0], {
      creator: getCurrentSocialProfile().attributes,
      forumType,
    });

    const newPost = addAnswerFields(forumType, newPostWithoutFields, parentPost.questionId);

    if (parentPost.type === 'answer') {
      const forumAnswerId = parentPost.topLevelForumAnswerId;

      if (!this.comments[forumAnswerId]) {
        this.comments[forumAnswerId] = {};
      }

      if (!this.comments[forumAnswerId][1]) {
        this.comments[forumAnswerId][1] = [];
      }

      this.comments[forumAnswerId][1].unshift(newPost);

      if (!this.commentPageCount[forumAnswerId]) {
        this.commentPageCount[forumAnswerId] = 1;
      }

      if (!this.commentPageRange[forumAnswerId]) {
        this.commentPageRange[forumAnswerId] = {
          startPage: 1,
          endPage: 1,
        };
      }

      this.findPosts(parentPost).forEach((entry: $TSFixMe) => {
        entry.childAnswerCount += 1;
      });

      this.commentLoadingState[forumAnswerId] = loadingStates.DONE;
      this.emitChange();
    } else {
      if (parentPost) {
        this.findPosts(parentPost).forEach((entry: $TSFixMe) => {
          if (entry && entry.topLevelAnswerCount !== undefined) {
            entry.topLevelAnswerCount += 1;
          }
        });
      }
      this.answerToInsert = newPost;
    }
  },

  SHOW_REPLY_EDITOR({ reply, reason, dontNotify }: $TSFixMe) {
    if (reply.highlighted) {
      reply.showEditor = true;
      reply.editReason = reason;
      reply.dontNotify = dontNotify;
    } else {
      this.findPosts(reply).forEach((post: $TSFixMe) => {
        post.showEditor = true;
        post.editReason = reason;
        post.dontNotify = dontNotify;
      });
    }

    this.emitChange();
  },

  HIDE_REPLY_EDITOR({ reply }: $TSFixMe) {
    if (reply.highlighted) {
      reply.showEditor = false;
      reply.editReason = '';
      reply.dontNotify = false;
    } else {
      this.findPosts(reply).forEach((post: $TSFixMe) => {
        post.showEditor = false;
        post.editReason = '';
        post.dontNotify = false;
      });
    }

    this.emitChange();
  },

  EDIT_REPLY({ reply, content }: $TSFixMe) {
    this.findPosts(reply, true).forEach((post: $TSFixMe) => {
      post.content = content;
      post.state.edited = {};
      post.state.edited.userId = this.getUserId();
    });

    this.emitChange();
  },

  EDIT_THREAD({ questionId, question, details }: $TSFixMe) {
    const { thread } = this.data[questionId];

    thread.content.question = question;
    thread.content.details = details;
    thread.state.edited = {};
    thread.state.edited.userId = this.getUserId();

    this.emitChange();
  },

  DELETE_POST({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      entry.hide = true;
      entry.state.deleted = {};
      entry.state.deleted.userId = this.getUserId();
    });

    this.emitChange();
  },

  SOFT_DELETE_POST({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      entry.state.deleted = {};
      entry.state.deleted.userId = this.getUserId();
    });

    this.emitChange();
  },

  UNDO_DELETE({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      entry.state.deleted = null;
    });

    this.emitChange();
  },

  FLAG_POST({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      if (entry.flagDetails) {
        entry.flagDetails.isActive = true;
      } else {
        entry.flagDetails = {
          isActive: true,
        };
      }
    });

    this.emitChange();
  },

  UNFLAG_POST({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      entry.flagDetails.isActive = false;
    });

    this.emitChange();
  },

  SHOW_ADMIN_DETAILS({ post }: $TSFixMe) {
    if (post.highlighted) {
      post.showAdminDetails = true;
    } else {
      this.findPosts(post).forEach((entry: $TSFixMe) => {
        entry.showAdminDetails = true;
      });
    }

    this.emitChange();
  },

  HIDE_ADMIN_DETAILS({ post }: $TSFixMe) {
    if (post.highlighted) {
      post.showAdminDetails = false;
    } else {
      this.findPosts(post).forEach((entry: $TSFixMe) => {
        entry.showAdminDetails = false;
      });
    }

    this.emitChange();
  },

  UPVOTE({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      entry.isUpvoted = true;

      if (entry.upvotes !== undefined) {
        entry.upvotes += 1;
      } else {
        entry.upvoteCount += 1;
      }

      entry.upvoteError = false;
    });

    this.emitChange();
  },

  CANCEL_UPVOTE({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      entry.isUpvoted = false;

      if (entry.upvotes !== undefined) {
        entry.upvotes -= 1;
      } else {
        entry.upvoteCount -= 1;
      }

      entry.upvoteError = false;
    });

    this.emitChange();
  },

  UPVOTE_ERROR({ post }: $TSFixMe) {
    this.findPosts(post, true).forEach((entry: $TSFixMe) => {
      entry.upvoteError = true;
    });

    this.emitChange();
  },

  FOLLOW({ question }: { question: { questionId: string } }) {
    const forumData = this.data[question.questionId];
    if (!forumData?.thread) {
      return;
    }

    const { thread } = forumData;

    thread.isFollowing = true;

    if (thread.followerCount === undefined) {
      thread.followCount += 1;
    } else {
      thread.followerCount += 1;
    }

    thread.followError = false;
    this.emitChange();
  },

  UNFOLLOW({ question }: $TSFixMe) {
    const { thread } = this.data[question.questionId];

    if (!thread) {
      return;
    }

    thread.isFollowing = false;

    if (thread.followerCount === undefined) {
      thread.followCount -= 1;
    } else {
      thread.followerCount -= 1;
    }

    thread.followError = false;
    this.emitChange();
  },

  HIGHLIGHT({ post }: $TSFixMe) {
    this.loadHighlightedPostHelper(Object.assign({}, post), post.questionId);
    this.emitChange();
  },

  UNHIGHLIGHT({ post }: $TSFixMe) {
    delete this.highlightedPosts[post.questionId];
    this.emitChange();
  },

  FOLLOW_ERROR({ question }: $TSFixMe) {
    const { thread } = this.data[question.questionId];
    thread.followError = true;

    this.emitChange();
  },

  SET_SAVING_STATE({ savingState, id, retry }: $TSFixMe) {
    this.savingStates[id] = savingState;
    this.retryCml[id] = retry;

    this.emitChange();
  },

  UNSET_QUESTION({ questionId }: $TSFixMe) {
    this.data[questionId] = {};
    this.emitChange();
  },

  QUESTION_LOADING_STATE({ questionId, loadingState }: $TSFixMe) {
    this.questionLoadingState[questionId] = loadingState;
    this.emitChange();
  },

  ANSWER_LOADING_STATE({ questionId, sort, page, loadingState }: $TSFixMe) {
    this.answerLoadingState[questionId + sort + page] = loadingState;
    this.emitChange();
  },

  COMMENT_LOADING_STATE({ answerId, loadingState }: $TSFixMe) {
    this.commentLoadingState[answerId] = loadingState;
    this.emitChange();
  },
};

export default ThreadDetailsStore;
